import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createMedicationServiceClient, type UpcomingDose } from "./client";

export type UpcomingDosesStatus = "idle" | "loading" | "ready" | "error";

// Read-only loader for surfaces that only need upcoming medication doses (Home, Upcoming Care).
// Calls only GET /v1/doses/upcoming -- no medication-list request. Same hook conventions as
// useMedications: runtime-configured base URL, auth.user access token, AbortSignal, loading/error
// state, no cache.
export function useUpcomingDoses() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.medicationService?.apiBaseUrl;
  const [upcomingDoses, setUpcomingDoses] = useState<UpcomingDose[]>([]);
  const [status, setStatus] = useState<UpcomingDosesStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createMedicationServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadUpcomingDoses = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        setUpcomingDoses(await client.listUpcomingDoses(signal));
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your medication records.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadUpcomingDoses(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadUpcomingDoses]);

  return {
    error,
    loadUpcomingDoses,
    status,
    upcomingDoses,
  };
}
